import { useEffect, useId, useMemo, useState, type FormEvent, type ReactNode } from 'react';

import { useCreditMutations, useSow, useSowAudit, useSowWriteAccess } from '../../../hooks/useApi';
import type {
    CreateCreditEventRequest,
    LedgerEntrySummary,
    SowCreditPoolSummary,
    SowSummary,
} from '../../../services/api';
import { cn } from '../../../lib/utils';
import { Button } from '../../ui/button';
import { formFieldGroupSurfaceClassName } from '../../ui/formPrimitives';
import { Input } from '../../ui/input';
import { Select } from '../../ui/select';
import { overlineClassName } from '../../ui/typography';
import { CreditCopyButton } from './CreditCopyButton';
import {
    Actions,
    AUDIT_REASON_FIELD_HINT,
    DialogFrame,
    Field,
    FormErrorAlert,
} from './creditDialogPrimitives';
import {
    formatCredits,
    formatEnumLabel,
    formatLinkedArtifactsTypeLabel,
    getLedgerReason,
    getManualLedgerPoolArtifactMismatchMessage,
    getPoolLabel,
    getRefundableLedgerAmount,
    MANUAL_LEDGER_LINKED_ARTIFACT_TYPE_OPTIONS,
} from './adminCreditAdapters';
import { useTouchedFields } from '../../../lib/touchedFormValidation';
import { getMutationErrorMessage } from './creditDialogHelpers';
import { sortPoolTypesForDisplay } from './sowLedgerTableGrouping';
import { buildSowUserSearchOptions } from './sowUserSearch';

const MANUAL_LEDGER_FIELDS = [
    'description',
    'teamId',
    'linkedArtifactPrefix',
    'linkedArtifactId',
    'poolType',
    'amount',
    'customTimestamp',
] as const;

type ManualLedgerFieldName = (typeof MANUAL_LEDGER_FIELDS)[number];

function getManualLedgerFieldErrors(values: {
    description: string;
    teamId: string;
    teamRequired: boolean;
    linkedArtifactPrefix: string;
    linkedArtifactId: string;
    poolType: string;
    amount: string;
    useCustomTimestamp: boolean;
    customTimestamp: string;
}): Partial<Record<ManualLedgerFieldName, string>> {
    const errors: Partial<Record<ManualLedgerFieldName, string>> = {};

    if (!values.description.trim()) {
        errors.description = 'Description/Name is required.';
    }
    if (values.teamRequired && !values.teamId.trim()) {
        errors.teamId = 'Team is required.';
    }
    if (!values.linkedArtifactPrefix.trim()) {
        errors.linkedArtifactPrefix = 'Linked artifact type is required.';
    }
    if (!values.linkedArtifactId.trim()) {
        errors.linkedArtifactId = 'Linked artifact ID is required.';
    }
    if (!values.poolType) {
        errors.poolType = 'Pool type is required.';
    }

    const poolArtifactMismatch = getManualLedgerPoolArtifactMismatchMessage(
        values.poolType,
        values.linkedArtifactPrefix,
    );
    if (poolArtifactMismatch) {
        errors.poolType = poolArtifactMismatch;
    }

    const parsedAmount = Number(values.amount);
    if (!values.amount.trim()) {
        errors.amount = 'Amount is required.';
    } else if (!Number.isFinite(parsedAmount)) {
        errors.amount = 'Amount must be numeric.';
    } else if (parsedAmount <= 0) {
        errors.amount = 'Amount of usage must be a positive number.';
    }

    if (values.useCustomTimestamp && !parseDateTimeLocalValue(values.customTimestamp)) {
        errors.customTimestamp = 'Custom timestamp must be a valid date and time.';
    }

    return errors;
}

function formatDateTimeLocalValue(date: Date): string {
    const pad = (value: number) => String(value).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function parseDateTimeLocalValue(value: string): Date | null {
    const parsed = new Date(value);
    return Number.isFinite(parsed.getTime()) ? parsed : null;
}

function formatLedgerPrefillAmountTwoDecimals(amount: string | undefined | null): string {
    if (amount === undefined || amount === null || amount.trim() === '') return '';
    const n = Number(amount);
    if (!Number.isFinite(n)) return String(amount);
    return n.toFixed(2);
}

function newManualLedgerIdempotencyKey(sowId: string): string {
    return `manual-${sowId}-${Date.now()}`;
}

function formatAuditJson(value: Record<string, unknown> | null): string {
    if (value === null) return '—';
    try {
        return JSON.stringify(value, null, 2);
    } catch {
        return String(value);
    }
}

const LEDGER_ACTION_BUTTON_PROPS = {
    grant: { variant: 'default' as const, className: undefined },
    adjustment: { variant: 'destructive' as const, className: 'font-semibold' },
    refund: { variant: 'destructive' as const, className: 'font-semibold' },
};

export function LedgerEventDialog({
    mode,
    open,
    onClose,
    sowId,
    pools,
    ledgerEntries = [],
    sourceLedgerEntry,
    grantDefaultPoolType,
}: {
    mode: 'grant' | 'adjustment' | 'refund';
    open: boolean;
    onClose: () => void;
    sowId: string;
    pools: SowCreditPoolSummary[];
    ledgerEntries?: LedgerEntrySummary[];
    sourceLedgerEntry?: LedgerEntrySummary | null;
    grantDefaultPoolType?: string;
}) {
    const { createCreditEvent, adjustCreditReservation } = useCreditMutations();
    const [poolType, setPoolType] = useState<string>(pools[0]?.poolType ?? 'pro_credit');
    const [amount, setAmount] = useState('');
    const [reason, setReason] = useState('');
    const [description, setDescription] = useState('');
    const [refundAmountMode, setRefundAmountMode] = useState<'full' | 'specific'>('full');
    const [error, setError] = useState<string | null>(null);

    const copy = {
        grant: {
            title: 'Grant credits',
            description: 'Adds a new grant event to the ledger. The reason is stored for auditing.',
            button: 'Create grant',
        },
        adjustment: {
            title: sourceLedgerEntry ? 'Adjust ledger event' : 'Manual ledger adjustment',
            description:
                'Records an append-only correction. Existing ledger rows are never rewritten. The reason is stored for auditing.',
            button: 'Record adjustment',
        },
        refund: {
            title: 'Refund usage',
            description:
                'Records an append-only refund event against usage. Existing ledger rows are never rewritten. The reason is stored for auditing.',
            button: 'Record refund',
        },
    }[mode];

    useEffect(() => {
        if (!open) return;
        const fallbackPool = pools[0]?.poolType ?? 'pro_credit';
        const grantPreferredPool =
            grantDefaultPoolType && pools.some((pool) => pool.poolType === grantDefaultPoolType)
                ? grantDefaultPoolType
                : fallbackPool;
        if (mode === 'grant') {
            setPoolType(grantPreferredPool);
        } else if (sourceLedgerEntry) {
            setPoolType(sourceLedgerEntry.poolType || fallbackPool);
        } else {
            setPoolType(fallbackPool);
        }
        setAmount('');
        if (mode === 'refund') {
            setRefundAmountMode('full');
        }
        setReason('');
        setDescription('');
        setError(null);
    }, [open, mode, sourceLedgerEntry?.id, pools, sourceLedgerEntry, grantDefaultPoolType]);

    const refundFullAmount = useMemo(() => {
        if (!sourceLedgerEntry) return '';
        const n = getRefundableLedgerAmount(sourceLedgerEntry, ledgerEntries);
        if (!Number.isFinite(n)) return '';
        return String(n);
    }, [ledgerEntries, sourceLedgerEntry]);

    const refundFullCreditsDisplay = useMemo(() => {
        if (!refundFullAmount) return '';
        const n = Number(refundFullAmount);
        if (!Number.isFinite(n)) return '';
        return formatLedgerPrefillAmountTwoDecimals(String(n));
    }, [refundFullAmount]);

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        if (mode === 'grant') {
            if (
                !reason.trim() ||
                !description.trim() ||
                !poolType.trim() ||
                !String(amount).trim() ||
                !Number.isFinite(Number(amount)) ||
                Number(amount) <= 0
            ) {
                setError('Fill in all required fields with valid values.');
                return;
            }
        }
        if (mode === 'adjustment' || mode === 'refund') {
            if (!reason.trim()) {
                setError('Reason is required.');
                return;
            }
        }
        if (mode === 'adjustment' && !String(amount).trim()) {
            setError('Usage change is required.');
            return;
        }
        if (mode === 'refund') {
            if (sourceLedgerEntry && refundAmountMode === 'full') {
                const fullAmt = Number(refundFullAmount);
                if (!Number.isFinite(fullAmt) || fullAmt <= 0) {
                    setError('No refundable amount is available for this entry.');
                    return;
                }
            } else if (
                !String(amount).trim() ||
                !Number.isFinite(Number(amount)) ||
                Number(amount) <= 0
            ) {
                setError('Enter a valid refund amount.');
                return;
            }
        }

        const row = sourceLedgerEntry ?? undefined;
        const linked = row !== undefined && (mode === 'adjustment' || mode === 'refund');
        const idempotencyKey = linked ? `${mode}-${row.id}-${Date.now()}` : undefined;
        const adjustsPendingReservation =
            mode === 'adjustment' &&
            row?.state === 'pending' &&
            typeof row.creditReservationId === 'string' &&
            row.creditReservationId !== '';
        const basedOnLedgerEntryId = linked && mode === 'adjustment' ? row.id : undefined;
        const linkedLedgerEntryId = linked && mode === 'refund' ? row.id : undefined;
        const descriptionPayload =
            mode === 'adjustment' ? 'adjustment' : mode === 'refund' ? 'refund' : description;
        const amountPayload =
            mode === 'refund' && sourceLedgerEntry && refundAmountMode === 'full'
                ? String(-Number(refundFullAmount))
                : mode === 'refund'
                  ? String(-Math.abs(Number(amount)))
                  : amount;

        setError(null);
        try {
            if (adjustsPendingReservation && row) {
                await adjustCreditReservation.mutateAsync({
                    reservationId: row.creditReservationId!,
                    request: {
                        idempotencyKey: idempotencyKey ?? `${mode}-${sowId}-${Date.now()}`,
                        amount: amountPayload,
                        auditReason: reason,
                        sourceSystem: 'admin-portal',
                    },
                });
            } else {
                const request: CreateCreditEventRequest = {
                    kind: mode,
                    sourceSystem: 'admin-portal',
                    idempotencyKey: idempotencyKey ?? `${mode}-${sowId}-${Date.now()}`,
                    sowId,
                    poolType,
                    amount: amountPayload,
                    auditReason: reason,
                    description: descriptionPayload,
                    basedOnLedgerEntryId,
                    linkedLedgerEntryId,
                };

                await createCreditEvent.mutateAsync(request);
            }
            onClose();
        } catch (err) {
            setError(getMutationErrorMessage(err, `Could not record ${mode}. Try again.`));
        }
    };

    const amountLabel =
        mode === 'adjustment' ? 'Usage change' : mode === 'refund' ? 'Usage' : 'Amount';
    const strictLedgerForm = mode === 'adjustment' || mode === 'refund';
    const actionButtonProps = LEDGER_ACTION_BUTTON_PROPS[mode];

    return (
        <DialogFrame
            open={open}
            onClose={onClose}
            title={copy.title}
            description={copy.description}
        >
            <form className="space-y-4" onSubmit={handleSubmit}>
                {error ? <FormErrorAlert message={error} /> : null}
                {sourceLedgerEntry ? (
                    <p className="text-xs text-neutral-500">
                        Linked entry:{' '}
                        <span className="font-mono text-neutral-700">{sourceLedgerEntry.id}</span>
                    </p>
                ) : null}
                <Field label="Pool type" required={strictLedgerForm || mode === 'grant'}>
                    <Select
                        ariaLabel="Pool type"
                        options={pools.map((pool) => ({
                            value: pool.poolType,
                            label: getPoolLabel(pool.poolType),
                        }))}
                        value={poolType}
                        onValueChange={setPoolType}
                        disabled={Boolean(
                            sourceLedgerEntry && (mode === 'refund' || mode === 'adjustment'),
                        )}
                    />
                </Field>
                {mode === 'grant' ? (
                    <Field label={amountLabel} required>
                        <div className="max-w-[16rem]">
                            <Input
                                value={amount}
                                onChange={(event) => setAmount(event.target.value)}
                                inputMode="decimal"
                                unit="credits"
                            />
                        </div>
                    </Field>
                ) : null}
                {mode === 'adjustment' ? (
                    <Field label={amountLabel} required={strictLedgerForm}>
                        <Input
                            value={amount}
                            onChange={(event) => setAmount(event.target.value)}
                            unit="credits"
                        />
                        <span className="block text-xs text-neutral-600">
                            Enter the delta (how many credits change), not the balance after the
                            change.{' '}
                            <span className="font-bold text-neutral-900">
                                A negative value reduces usage and lowers the cost of the entry; a
                                positive value increases usage.
                            </span>
                        </span>
                    </Field>
                ) : null}
                {mode === 'refund' ? (
                    <div className="space-y-3">
                        {sourceLedgerEntry ? (
                            <>
                                <Field as="group" label="Refund amount">
                                    <label className="flex cursor-pointer items-start gap-2 text-sm">
                                        <input
                                            type="radio"
                                            name="ledger-refund-amount-mode"
                                            className="mt-1 rounded-full border-neutral-300"
                                            checked={refundAmountMode === 'full'}
                                            onChange={() => {
                                                setRefundAmountMode('full');
                                                setAmount('');
                                            }}
                                        />
                                        <span>
                                            Full refund ({refundFullCreditsDisplay || '—'} credits)
                                        </span>
                                    </label>
                                    <label className="flex cursor-pointer items-start gap-2 text-sm">
                                        <input
                                            type="radio"
                                            name="ledger-refund-amount-mode"
                                            className="mt-1 rounded-full border-neutral-300"
                                            checked={refundAmountMode === 'specific'}
                                            onChange={() => setRefundAmountMode('specific')}
                                        />
                                        <span>Partial refund</span>
                                    </label>
                                </Field>
                                {refundAmountMode === 'specific' ? (
                                    <Field label="Credits to refund" required>
                                        <div className="max-w-[16rem]">
                                            <Input
                                                value={amount}
                                                onChange={(event) => setAmount(event.target.value)}
                                                inputMode="decimal"
                                                unit="credits"
                                            />
                                        </div>
                                    </Field>
                                ) : null}
                            </>
                        ) : (
                            <Field label={amountLabel} required={strictLedgerForm}>
                                <div className="max-w-[16rem]">
                                    <Input
                                        value={amount}
                                        onChange={(event) => setAmount(event.target.value)}
                                        inputMode="decimal"
                                        unit="credits"
                                    />
                                </div>
                            </Field>
                        )}
                    </div>
                ) : null}
                {mode === 'grant' ? (
                    <>
                        <Field label="Description" required>
                            <Input
                                value={description}
                                onChange={(event) => setDescription(event.target.value)}
                            />
                        </Field>
                        <Field label="Reason" required hint={AUDIT_REASON_FIELD_HINT}>
                            <Input
                                value={reason}
                                onChange={(event) => setReason(event.target.value)}
                            />
                        </Field>
                    </>
                ) : (
                    <Field label="Reason" required hint={AUDIT_REASON_FIELD_HINT}>
                        <Input value={reason} onChange={(event) => setReason(event.target.value)} />
                    </Field>
                )}
                <Actions onCancel={onClose}>
                    <Button
                        type="submit"
                        {...actionButtonProps}
                        loading={createCreditEvent.isPending || adjustCreditReservation.isPending}
                        disabled={createCreditEvent.isPending || adjustCreditReservation.isPending}
                    >
                        {createCreditEvent.isPending || adjustCreditReservation.isPending
                            ? 'Saving…'
                            : copy.button}
                    </Button>
                </Actions>
            </form>
        </DialogFrame>
    );
}

export function ManualLedgerEntryDialog({
    open,
    onClose,
    sowId,
    pools,
    teams,
}: {
    open: boolean;
    onClose: () => void;
    sowId: string;
    pools: SowCreditPoolSummary[];
    teams: Array<{ teamId: number; label: string }>;
}) {
    const { createCreditEvent } = useCreditMutations();
    const statusId = useId();
    const writeAccessQuery = useSowWriteAccess(sowId, { enabled: open });
    const { touch, touchAll, resetTouched, fieldError } = useTouchedFields<ManualLedgerFieldName>();
    const [manualIdempotencyKey, setManualIdempotencyKey] = useState('');
    const [linkedArtifactPrefix, setLinkedArtifactPrefix] = useState('');
    const [linkedArtifactId, setLinkedArtifactId] = useState('');
    const [reason, setReason] = useState('');
    const [teamId, setTeamId] = useState('');
    const [attributedUserId, setAttributedUserId] = useState('');
    const [poolType, setPoolType] = useState<string>(pools[0]?.poolType ?? '');
    const [amount, setAmount] = useState('');
    const [description, setDescription] = useState('');
    const [useCustomTimestamp, setUseCustomTimestamp] = useState(false);
    const [customTimestamp, setCustomTimestamp] = useState('');
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!open) return;
        setManualIdempotencyKey(newManualLedgerIdempotencyKey(sowId));
        setLinkedArtifactPrefix('');
        setLinkedArtifactId('');
        setReason('');
        setTeamId(teams[0] ? String(teams[0].teamId) : '');
        setAttributedUserId('');
        setPoolType(pools[0]?.poolType ?? '');
        setAmount('');
        setDescription('');
        setUseCustomTimestamp(false);
        setCustomTimestamp(formatDateTimeLocalValue(new Date()));
        setError(null);
        resetTouched();
    }, [open, sowId, pools, teams, resetTouched]);

    const fieldErrors = useMemo(
        () =>
            getManualLedgerFieldErrors({
                description,
                teamId,
                teamRequired: teams.length > 0,
                linkedArtifactPrefix,
                linkedArtifactId,
                poolType,
                amount,
                useCustomTimestamp,
                customTimestamp,
            }),
        [
            description,
            teamId,
            linkedArtifactPrefix,
            linkedArtifactId,
            poolType,
            amount,
            useCustomTimestamp,
            customTimestamp,
            teams.length,
        ],
    );
    const manualLedgerFormComplete = Object.keys(fieldErrors).length === 0;
    const eligibleUserOptions = useMemo(
        () => buildSowUserSearchOptions(writeAccessQuery.data?.writeAccess.eligibleUsers ?? []),
        [writeAccessQuery.data?.writeAccess.eligibleUsers],
    );

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        setError(null);
        touchAll(MANUAL_LEDGER_FIELDS);
        if (!manualLedgerFormComplete) {
            return;
        }

        const parsedTeamId = teamId.trim() ? Number(teamId) : null;

        let occurredAt: string | undefined;
        if (useCustomTimestamp) {
            const parsedTimestamp = parseDateTimeLocalValue(customTimestamp);
            if (!parsedTimestamp) {
                return;
            }
            occurredAt = parsedTimestamp.toISOString();
        }
        const timestampFields = occurredAt ? { occurredAt } : {};
        const attributionFields = attributedUserId
            ? { attributedUserId: Number(attributedUserId) }
            : {};
        const selectedLinkedArtifactType = linkedArtifactPrefix.trim();

        const request: CreateCreditEventRequest = {
            kind: 'usage',
            sourceSystem: 'admin-portal',
            idempotencyKey: manualIdempotencyKey.trim(),
            eventType: selectedLinkedArtifactType || null,
            ...timestampFields,
            ...attributionFields,
            sowId,
            teamId: parsedTeamId,
            poolType,
            amount,
            description: description.trim(),
            auditReason: reason.trim() || null,
            linkedArtifactType: selectedLinkedArtifactType,
            linkedArtifactId: linkedArtifactId.trim(),
        };
        setError(null);
        try {
            await createCreditEvent.mutateAsync(request);
            onClose();
        } catch (err) {
            setError(
                getMutationErrorMessage(err, 'Could not create manual usage entry. Try again.'),
            );
        }
    };

    return (
        <DialogFrame
            open={open}
            onClose={onClose}
            title="Manual usage entry"
            description="Creates a new usage event only."
        >
            <form
                className="space-y-5"
                onSubmit={handleSubmit}
                onKeyDown={(event) => {
                    if (
                        event.key === 'Enter' &&
                        event.target instanceof HTMLInputElement &&
                        !manualLedgerFormComplete
                    ) {
                        touchAll(MANUAL_LEDGER_FIELDS);
                    }
                }}
            >
                {error ? <FormErrorAlert message={error} /> : null}
                <div className="space-y-3">
                    <Field
                        label="Description/Name"
                        required
                        error={fieldError('description', fieldErrors.description)}
                    >
                        <Input
                            value={description}
                            onChange={(event) => setDescription(event.target.value)}
                            onBlur={() => touch('description')}
                        />
                    </Field>
                    <div className="grid gap-4 md:grid-cols-2">
                        <Field label="Source">
                            <Input value="Admin portal manual entry" disabled />
                        </Field>
                        <Field label="SOW ID">
                            <Input value={sowId} disabled />
                        </Field>
                    </div>
                    <div className="grid gap-4 md:grid-cols-2">
                        <Field
                            label="Team"
                            required={teams.length > 0}
                            error={fieldError('teamId', fieldErrors.teamId)}
                        >
                            <Select
                                ariaLabel="Team"
                                options={
                                    teams.length === 0
                                        ? [{ value: '', label: 'No team' }]
                                        : teams.map((team) => ({
                                              value: String(team.teamId),
                                              label: `${team.label} (#${team.teamId})`,
                                          }))
                                }
                                value={teamId}
                                onValueChange={(value) => {
                                    setTeamId(value);
                                    touch('teamId');
                                }}
                                onBlur={() => touch('teamId')}
                                disabled={teams.length <= 1}
                            />
                        </Field>
                        <Field
                            label="Pool type"
                            required
                            error={fieldError('poolType', fieldErrors.poolType)}
                        >
                            <Select
                                ariaLabel="Pool type"
                                options={pools.map((pool) => ({
                                    value: pool.poolType,
                                    label: getPoolLabel(pool.poolType),
                                }))}
                                value={poolType}
                                onValueChange={(value) => {
                                    setPoolType(value);
                                    touch('poolType');
                                    if (linkedArtifactPrefix.trim()) {
                                        touch('linkedArtifactPrefix');
                                    }
                                }}
                                onBlur={() => touch('poolType')}
                            />
                        </Field>
                    </div>
                    <Field
                        label="Attributed user"
                        hint={
                            writeAccessQuery.isError
                                ? 'Eligible users could not be loaded. If left blank, this usage will be attributed to the current user.'
                                : 'Optional. If left blank, this usage will be attributed to the current user.'
                        }
                    >
                        <Select
                            ariaLabel="Attributed user"
                            searchable
                            searchPlaceholder="Search by name, email, or user ID"
                            placeholder={
                                writeAccessQuery.isLoading
                                    ? 'Loading eligible users…'
                                    : 'Current user'
                            }
                            options={eligibleUserOptions.map((user) => ({
                                value: String(user.userId),
                                label: `${user.label}${user.email ? ` (${user.email})` : ''} (#${user.userId})`,
                                searchText: user.searchText,
                            }))}
                            value={attributedUserId}
                            onValueChange={setAttributedUserId}
                            disabled={writeAccessQuery.isLoading || writeAccessQuery.isError}
                        />
                    </Field>
                    <div className="grid gap-4 md:grid-cols-2">
                        <Field label="Entry type">
                            <Input value="Usage" disabled />
                        </Field>
                        <Field label="State">
                            <Input value="Done" disabled />
                        </Field>
                    </div>
                    <Field
                        label="Amount of usage"
                        required
                        error={fieldError('amount', fieldErrors.amount)}
                        hint="Enter credits consumed as a positive number; this records a positive usage event."
                    >
                        <div className="max-w-[16rem]">
                            <Input
                                value={amount}
                                onChange={(event) => setAmount(event.target.value)}
                                onBlur={() => touch('amount')}
                                inputMode="decimal"
                                unit="credits"
                            />
                        </div>
                    </Field>
                </div>
                <div className={cn(formFieldGroupSurfaceClassName, 'space-y-3')}>
                    <div>
                        <p className="text-sm font-semibold text-neutral-950">Linked artifact</p>
                        <p className="mt-1 text-xs text-neutral-600">
                            Tie this charge back to the specific work it belongs to (study, project,
                            session, or other deliverable).
                        </p>
                    </div>
                    <div className="grid gap-4 md:grid-cols-2">
                        <Field
                            label="Type"
                            required
                            error={fieldError(
                                'linkedArtifactPrefix',
                                fieldErrors.linkedArtifactPrefix,
                            )}
                        >
                            <Select
                                ariaLabel="Linked artifact type"
                                options={[
                                    { value: '', label: 'Select linked artifact type' },
                                    ...MANUAL_LEDGER_LINKED_ARTIFACT_TYPE_OPTIONS,
                                ]}
                                value={linkedArtifactPrefix}
                                onValueChange={(value) => {
                                    setLinkedArtifactPrefix(value);
                                    touch('linkedArtifactPrefix');
                                    if (poolType) {
                                        touch('poolType');
                                    }
                                }}
                                onBlur={() => touch('linkedArtifactPrefix')}
                            />
                        </Field>
                        <Field
                            label="ID"
                            required
                            error={fieldError('linkedArtifactId', fieldErrors.linkedArtifactId)}
                        >
                            <Input
                                value={linkedArtifactId}
                                onChange={(event) => setLinkedArtifactId(event.target.value)}
                                onBlur={() => touch('linkedArtifactId')}
                            />
                        </Field>
                    </div>
                </div>
                <Field label="Reason" hint={AUDIT_REASON_FIELD_HINT}>
                    <Input value={reason} onChange={(event) => setReason(event.target.value)} />
                </Field>
                <div className="space-y-2">
                    <label className="flex cursor-pointer items-center gap-2 text-sm font-semibold text-neutral-950">
                        <input
                            type="checkbox"
                            checked={useCustomTimestamp}
                            onChange={(event) => setUseCustomTimestamp(event.target.checked)}
                            className="rounded border-neutral-300"
                        />
                        <span>Custom timestamp (defaults to current time)</span>
                    </label>
                    {useCustomTimestamp ? (
                        <Field
                            label="Timestamp"
                            error={fieldError('customTimestamp', fieldErrors.customTimestamp)}
                        >
                            <Input
                                type="datetime-local"
                                value={customTimestamp}
                                onChange={(event) => setCustomTimestamp(event.target.value)}
                                onBlur={() => touch('customTimestamp')}
                            />
                        </Field>
                    ) : null}
                </div>
                <Actions
                    onCancel={onClose}
                    status={
                        !createCreditEvent.isPending &&
                        pools.length > 0 &&
                        !manualLedgerFormComplete
                            ? 'Fill in all required fields with valid values.'
                            : undefined
                    }
                    statusId={statusId}
                >
                    <Button
                        type="submit"
                        loading={createCreditEvent.isPending}
                        disabled={
                            createCreditEvent.isPending ||
                            pools.length === 0 ||
                            !manualLedgerFormComplete
                        }
                        aria-describedby={
                            !createCreditEvent.isPending &&
                            pools.length > 0 &&
                            !manualLedgerFormComplete
                                ? statusId
                                : undefined
                        }
                    >
                        {createCreditEvent.isPending ? 'Saving…' : 'Create usage event'}
                    </Button>
                </Actions>
            </form>
        </DialogFrame>
    );
}

function resolveSowPoolTypes(selectedSow: SowSummary | undefined): string[] {
    return sortPoolTypesForDisplay((selectedSow?.CreditPools ?? []).map((pool) => pool.poolType));
}

function defaultDestinationPoolType(
    selectedSow: SowSummary | undefined,
    preferredPoolType: string,
): string {
    const poolTypes = resolveSowPoolTypes(selectedSow);
    if (poolTypes.includes(preferredPoolType)) {
        return preferredPoolType;
    }
    return poolTypes[0] ?? preferredPoolType;
}

const ROLLOVER_DEFAULT_DESCRIPTION = 'Credit rollover';

const ROLLOVER_FIELDS = ['destinationSowId', 'poolType', 'amount', 'description'] as const;

type RolloverFieldName = (typeof ROLLOVER_FIELDS)[number];

function getRolloverFieldErrors(values: {
    destinationSowId: string;
    poolType: string;
    amount: string;
    description: string;
    maxRolloverAmount: number;
}): Partial<Record<RolloverFieldName, string>> {
    const errors: Partial<Record<RolloverFieldName, string>> = {};

    if (!values.destinationSowId.trim()) {
        errors.destinationSowId = 'Select a destination SOW.';
    }
    if (!values.poolType.trim()) {
        errors.poolType = 'Select a pool type.';
    }
    if (!values.description.trim()) {
        errors.description = 'Description is required.';
    }

    const parsedAmount = Number(values.amount);
    if (!values.amount.trim()) {
        errors.amount = 'Amount is required.';
    } else if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
        errors.amount = 'Amount must be a positive number.';
    } else if (parsedAmount > values.maxRolloverAmount) {
        errors.amount = `Amount cannot exceed ${formatCredits(values.maxRolloverAmount)} available in this pool.`;
    }

    return errors;
}

function getPoolAvailableBalance(balance: SowSummary['balanceSummary'], poolType: string): number {
    const poolBalance = balance?.pools?.find((pool) => pool.poolType === poolType);
    if (!poolBalance) return 0;
    const available = Number(poolBalance.available);
    return Number.isFinite(available) && available > 0 ? available : 0;
}

export function ReassignLedgerDialog({
    open,
    onClose,
    sow,
    allSows,
    sourceLedgerEntry,
    ledgerEntries,
}: {
    open: boolean;
    onClose: () => void;
    sow: SowSummary;
    allSows: SowSummary[];
    sourceLedgerEntry: LedgerEntrySummary;
    ledgerEntries: LedgerEntrySummary[];
}) {
    const { createCreditEvent } = useCreditMutations();
    const sourcePoolType = sourceLedgerEntry.poolType;
    const targetSows = useMemo(
        () => allSows.filter((candidate) => candidate.companyId === sow.companyId),
        [allSows, sow.companyId],
    );
    const [destinationSowId, setDestinationSowId] = useState(sow.id);
    const [destinationPoolType, setDestinationPoolType] = useState(sourcePoolType);
    const [reason, setReason] = useState('');
    const [error, setError] = useState<string | null>(null);

    const destinationSowQuery = useSow(open ? destinationSowId : null);
    const destinationSowFromQuery = destinationSowQuery.data?.sow;
    const destinationSowWithPools = useMemo(() => {
        if (destinationSowFromQuery) {
            return destinationSowFromQuery;
        }
        if (destinationSowId === sow.id) {
            return sow;
        }
        return undefined;
    }, [destinationSowFromQuery, destinationSowId, sow]);
    const destinationPoolTypes = useMemo(
        () => resolveSowPoolTypes(destinationSowWithPools),
        [destinationSowWithPools],
    );
    const destinationPoolsLoading =
        open &&
        !!destinationSowId &&
        destinationSowId !== sow.id &&
        destinationSowQuery.isLoading &&
        !destinationSowFromQuery;

    const reassignAmount = useMemo(() => {
        const n = getRefundableLedgerAmount(sourceLedgerEntry, ledgerEntries);
        return Number.isFinite(n) && n > 0 ? n : 0;
    }, [ledgerEntries, sourceLedgerEntry]);

    const destinationUnchanged =
        destinationSowId === sow.id && destinationPoolType === sourcePoolType;

    useEffect(() => {
        if (!open) return;
        setDestinationSowId(sow.id);
        setDestinationPoolType(sourcePoolType);
        setReason('');
        setError(null);
    }, [open, sourceLedgerEntry.id, sourcePoolType, sow.id]);

    useEffect(() => {
        if (!open) return;
        setDestinationPoolType((current) =>
            destinationPoolTypes.includes(current)
                ? current
                : defaultDestinationPoolType(destinationSowWithPools, sourcePoolType),
        );
    }, [destinationPoolTypes, destinationSowWithPools, open, sourcePoolType]);

    const handleDestinationSowChange = (nextSowId: string) => {
        setDestinationSowId(nextSowId);
    };

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        if (!reason.trim()) {
            setError('Reason is required.');
            return;
        }
        if (
            !destinationSowId.trim() ||
            !destinationPoolType.trim() ||
            reassignAmount <= 0 ||
            destinationUnchanged
        ) {
            return;
        }

        setError(null);
        try {
            await createCreditEvent.mutateAsync({
                kind: 'reassign',
                sourceSystem: 'admin-portal',
                idempotencyKey: `reassign-${sourceLedgerEntry.id}-${Date.now()}`,
                linkedLedgerEntryId: sourceLedgerEntry.id,
                destinationSowId,
                poolType: destinationPoolType,
                auditReason: reason.trim(),
            });
            onClose();
        } catch (err) {
            setError(getMutationErrorMessage(err, 'Could not reassign usage. Try again.'));
        }
    };

    const submitDisabled =
        reassignAmount <= 0 ||
        destinationUnchanged ||
        destinationPoolsLoading ||
        destinationPoolTypes.length === 0;

    return (
        <DialogFrame
            open={open}
            onClose={onClose}
            title="Reassign usage"
            description="Moves the net completed usage for this ledger item (including linked adjustments and refunds) to another SOW or pool in the same company. Existing ledger rows are never rewritten. The reason is stored for auditing."
        >
            <form className="space-y-4" onSubmit={handleSubmit}>
                {error ? <FormErrorAlert message={error} /> : null}
                <p className="text-xs text-neutral-500">
                    Linked entry:{' '}
                    <span className="font-mono text-neutral-700">{sourceLedgerEntry.id}</span>
                </p>
                <p className="text-sm text-neutral-700">
                    Credits to move:{' '}
                    <span className="font-semibold tabular-nums text-neutral-950">
                        {formatCredits(reassignAmount)}
                    </span>
                </p>
                <Field label="Destination SOW" required>
                    <Select
                        ariaLabel="Destination SOW"
                        searchable
                        searchPlaceholder="Search by SOW name or ID"
                        options={targetSows.map((candidate) => ({
                            value: candidate.id,
                            label: candidate.name,
                            searchText: `${candidate.name} ${candidate.id}`,
                        }))}
                        value={destinationSowId}
                        onValueChange={handleDestinationSowChange}
                    />
                </Field>
                <Field label="Destination pool" required>
                    <Select
                        ariaLabel="Destination pool"
                        options={destinationPoolTypes.map((poolType) => ({
                            value: poolType,
                            label: getPoolLabel(poolType),
                        }))}
                        value={destinationPoolType}
                        onValueChange={setDestinationPoolType}
                    />
                    {destinationPoolsLoading ? (
                        <span className="block text-xs text-neutral-600">Loading pools…</span>
                    ) : destinationPoolTypes.length === 0 ? (
                        <span className="block text-xs text-neutral-600">
                            The selected SOW has no configured pools.
                        </span>
                    ) : destinationUnchanged ? (
                        <span className="block text-xs text-neutral-600">
                            Choose a different SOW or pool to reassign this usage.
                        </span>
                    ) : null}
                </Field>
                <Field label="Reason" required hint={AUDIT_REASON_FIELD_HINT}>
                    <Input value={reason} onChange={(event) => setReason(event.target.value)} />
                </Field>
                <Actions onCancel={onClose}>
                    <Button
                        type="submit"
                        loading={createCreditEvent.isPending}
                        disabled={createCreditEvent.isPending || submitDisabled}
                    >
                        {createCreditEvent.isPending ? 'Reassigning…' : 'Reassign usage'}
                    </Button>
                </Actions>
            </form>
        </DialogFrame>
    );
}

export function RolloverDialog({
    open,
    onClose,
    sow,
    allSows,
    initialPoolType,
}: {
    open: boolean;
    onClose: () => void;
    sow: SowSummary;
    allSows: SowSummary[];
    initialPoolType?: string;
}) {
    const { createCreditEvent } = useCreditMutations();
    const statusId = useId();
    const { touch, touchAll, resetTouched, fieldError } = useTouchedFields<RolloverFieldName>();
    const pools = useMemo(() => sow.CreditPools ?? [], [sow.CreditPools]);
    const targetSows = useMemo(
        () =>
            allSows.filter(
                (candidate) => candidate.companyId === sow.companyId && candidate.id !== sow.id,
            ),
        [allSows, sow.companyId, sow.id],
    );
    const [destinationSowId, setDestinationSowId] = useState(targetSows[0]?.id ?? '');
    const [poolType, setPoolType] = useState<string>(pools[0]?.poolType ?? 'pro_credit');
    const [amount, setAmount] = useState('');
    const [description, setDescription] = useState(ROLLOVER_DEFAULT_DESCRIPTION);
    const [reason, setReason] = useState('');
    const [error, setError] = useState<string | null>(null);

    const maxRolloverAmount = useMemo(
        () => getPoolAvailableBalance(sow.balanceSummary, poolType),
        [poolType, sow.balanceSummary],
    );

    useEffect(() => {
        if (!open) return;
        const fallback = pools[0]?.poolType ?? 'pro_credit';
        const nextPoolType =
            initialPoolType && pools.some((pool) => pool.poolType === initialPoolType)
                ? initialPoolType
                : fallback;
        setDestinationSowId(targetSows[0]?.id ?? '');
        setPoolType(nextPoolType);
        setAmount('');
        setDescription(ROLLOVER_DEFAULT_DESCRIPTION);
        setReason('');
        setError(null);
        resetTouched();
    }, [open, initialPoolType, pools, targetSows, resetTouched]);

    const fieldErrors = useMemo(
        () =>
            getRolloverFieldErrors({
                destinationSowId,
                poolType,
                amount,
                description,
                maxRolloverAmount,
            }),
        [destinationSowId, poolType, amount, description, maxRolloverAmount],
    );
    const rolloverFormComplete = Object.keys(fieldErrors).length === 0;
    const submitDisabled = targetSows.length === 0 || !rolloverFormComplete;

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        setError(null);
        touchAll(ROLLOVER_FIELDS);
        if (!rolloverFormComplete) {
            return;
        }

        try {
            await createCreditEvent.mutateAsync({
                kind: 'rollover',
                sourceSystem: 'admin-portal',
                idempotencyKey: `rollover-${sow.id}-${Date.now()}`,
                sowId: sow.id,
                destinationSowId,
                poolType,
                amount,
                description: description.trim(),
                auditReason: reason,
            });
            onClose();
        } catch (err) {
            setError(getMutationErrorMessage(err, 'Could not roll over credits. Try again.'));
        }
    };

    return (
        <DialogFrame
            open={open}
            onClose={onClose}
            title="Rollover"
            description="Transfers unused credits by recording append-only rollover events. Optional reason is kept for auditing."
        >
            <form
                className="space-y-4"
                onSubmit={handleSubmit}
                onKeyDown={(event) => {
                    if (
                        event.key === 'Enter' &&
                        event.target instanceof HTMLInputElement &&
                        !rolloverFormComplete
                    ) {
                        touchAll(ROLLOVER_FIELDS);
                    }
                }}
            >
                {error ? <FormErrorAlert message={error} /> : null}
                {targetSows.length === 0 ? (
                    <p className="text-sm text-neutral-600">
                        No other SOWs exist for this company to receive a rollover.
                    </p>
                ) : null}
                <Field
                    label="Destination SOW"
                    required
                    error={fieldError('destinationSowId', fieldErrors.destinationSowId)}
                >
                    <Select
                        ariaLabel="Destination SOW"
                        searchable
                        searchPlaceholder="Search by SOW name or ID"
                        options={targetSows.map((candidate) => ({
                            value: candidate.id,
                            label: candidate.name,
                            searchText: `${candidate.name} ${candidate.id}`,
                        }))}
                        value={destinationSowId}
                        onValueChange={(value) => {
                            setDestinationSowId(value);
                            touch('destinationSowId');
                        }}
                        onBlur={() => touch('destinationSowId')}
                    />
                </Field>
                <Field
                    label="Pool type"
                    required
                    error={fieldError('poolType', fieldErrors.poolType)}
                >
                    <Select
                        ariaLabel="Pool type"
                        options={pools.map((pool) => ({
                            value: pool.poolType,
                            label: getPoolLabel(pool.poolType),
                        }))}
                        value={poolType}
                        onValueChange={(value) => {
                            setPoolType(value);
                            touch('poolType');
                        }}
                        onBlur={() => touch('poolType')}
                    />
                </Field>
                <Field
                    label="Amount"
                    required
                    error={fieldError('amount', fieldErrors.amount)}
                    hint={`Maximum: ${formatCredits(maxRolloverAmount)} available in this pool.`}
                >
                    <div className="max-w-[16rem]">
                        <Input
                            value={amount}
                            onChange={(event) => setAmount(event.target.value)}
                            onBlur={() => touch('amount')}
                            inputMode="decimal"
                            unit="credits"
                        />
                    </div>
                </Field>
                <Field
                    label="Description"
                    required
                    error={fieldError('description', fieldErrors.description)}
                >
                    <Input
                        value={description}
                        onChange={(event) => setDescription(event.target.value)}
                        onBlur={() => touch('description')}
                    />
                </Field>
                <Field label="Reason" hint={AUDIT_REASON_FIELD_HINT}>
                    <Input value={reason} onChange={(event) => setReason(event.target.value)} />
                </Field>
                <Actions
                    onCancel={onClose}
                    status={
                        !createCreditEvent.isPending &&
                        targetSows.length > 0 &&
                        !rolloverFormComplete
                            ? 'Fill in all required fields with valid values.'
                            : undefined
                    }
                    statusId={statusId}
                >
                    <Button
                        type="submit"
                        loading={createCreditEvent.isPending}
                        disabled={createCreditEvent.isPending || submitDisabled}
                        aria-describedby={
                            !createCreditEvent.isPending &&
                            targetSows.length > 0 &&
                            !rolloverFormComplete
                                ? statusId
                                : undefined
                        }
                    >
                        {createCreditEvent.isPending ? 'Rolling over…' : 'Roll over credits'}
                    </Button>
                </Actions>
            </form>
        </DialogFrame>
    );
}

function LedgerDetailRow({ label, children }: { label: string; children: ReactNode }) {
    return (
        <div className="grid grid-cols-[8rem_1fr] gap-3 py-2">
            <dt className={overlineClassName}>{label}</dt>
            <dd className="text-sm text-neutral-900">{children}</dd>
        </div>
    );
}

function CopyableIdValue({ value, copyLabel }: { value: string; copyLabel: string }) {
    return (
        <div className="flex items-start gap-2">
            <span className="min-w-0 flex-1 font-mono text-xs break-all">{value}</span>
            <CreditCopyButton value={value} label={copyLabel} />
        </div>
    );
}

export function LedgerEntryDetailsDialog({
    entry,
    onClose,
}: {
    entry: LedgerEntrySummary | null;
    onClose: () => void;
}) {
    if (!entry) return null;

    const reason = getLedgerReason(entry);
    const linkedArtifactType = formatLinkedArtifactsTypeLabel(entry.linkedArtifactType);

    return (
        <DialogFrame
            open={entry !== null}
            onClose={onClose}
            title="Extra details"
            panelClassName="max-w-lg"
        >
            <dl className="divide-y divide-neutral-100">
                <LedgerDetailRow label="Entry ID">
                    <CopyableIdValue value={entry.id} copyLabel="Copy entry ID" />
                </LedgerDetailRow>
                <LedgerDetailRow label="Description">
                    {entry.description?.trim() ? (
                        <span className="whitespace-pre-wrap">{entry.description.trim()}</span>
                    ) : (
                        <span className="text-neutral-500">No description recorded.</span>
                    )}
                </LedgerDetailRow>
                <LedgerDetailRow label="Reason">
                    {reason ? (
                        <span className="whitespace-pre-wrap">{reason}</span>
                    ) : (
                        <span className="text-neutral-500">No audit reason recorded.</span>
                    )}
                </LedgerDetailRow>
                {linkedArtifactType && entry.linkedArtifactId ? (
                    <LedgerDetailRow label="Linked artifact">
                        <p className="mb-1 text-neutral-700">{linkedArtifactType}</p>
                        <CopyableIdValue
                            value={entry.linkedArtifactId}
                            copyLabel="Copy linked artifact ID"
                        />
                    </LedgerDetailRow>
                ) : null}
                {entry.linkedLedgerEntryId ? (
                    <LedgerDetailRow label="Linked entry">
                        <CopyableIdValue
                            value={entry.linkedLedgerEntryId}
                            copyLabel="Copy linked entry ID"
                        />
                    </LedgerDetailRow>
                ) : null}
                {entry.creditReservationId ? (
                    <LedgerDetailRow label="Reservation">
                        <CopyableIdValue
                            value={entry.creditReservationId}
                            copyLabel="Copy reservation ID"
                        />
                    </LedgerDetailRow>
                ) : null}
                {entry.sourceSystem ? (
                    <LedgerDetailRow label="Source">{entry.sourceSystem}</LedgerDetailRow>
                ) : null}
                {entry.createdByUserId != null ? (
                    <LedgerDetailRow label="Created by user ID">
                        {entry.createdByUserId}
                    </LedgerDetailRow>
                ) : null}
                <LedgerDetailRow label="Created">
                    {new Date(entry.createdAt).toLocaleString()}
                </LedgerDetailRow>
            </dl>
            <div className="mt-6 flex justify-end border-t border-neutral-100 pt-4">
                <Button type="button" onClick={onClose}>
                    Close
                </Button>
            </div>
        </DialogFrame>
    );
}

export function SowAuditDialog({
    open,
    onClose,
    sowId,
}: {
    open: boolean;
    onClose: () => void;
    sowId: string;
}) {
    const { data, isLoading, isError, error } = useSowAudit(sowId, { enabled: open });
    const entries = data?.auditEntries ?? [];

    return (
        <DialogFrame
            open={open}
            onClose={onClose}
            title="SOW audit history"
            description="Lifecycle and field changes recorded for this SOW."
            panelClassName="max-w-4xl"
        >
            {isLoading ? (
                <p className="text-sm text-neutral-500">Loading…</p>
            ) : isError ? (
                <p className="text-sm text-destructive">
                    {error instanceof Error
                        ? error.message
                        : 'Could not load audit history. Close and try again.'}
                </p>
            ) : entries.length === 0 ? (
                <p className="text-sm text-neutral-500">No audit entries yet.</p>
            ) : (
                <div className="space-y-6">
                    {entries.map((entry) => (
                        <div key={entry.id} className="rounded-lg border border-neutral-200 p-4">
                            <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-neutral-100 pb-3">
                                <div>
                                    <p className="font-medium text-neutral-950">
                                        {formatEnumLabel(entry.action)}
                                    </p>
                                    <p className="text-xs tabular-nums text-neutral-500">
                                        {new Date(entry.createdAt).toLocaleString()}
                                        {entry.createdByUserId != null
                                            ? ` · User #${entry.createdByUserId}`
                                            : ''}
                                    </p>
                                </div>
                            </div>
                            <div className="mt-3 grid gap-4 md:grid-cols-2">
                                <div>
                                    <p className={overlineClassName}>Before</p>
                                    <pre className="mt-1 max-h-48 overflow-auto rounded bg-neutral-50 p-2 text-xs text-neutral-800">
                                        {formatAuditJson(entry.preValues)}
                                    </pre>
                                </div>
                                <div>
                                    <p className={overlineClassName}>After</p>
                                    <pre className="mt-1 max-h-48 overflow-auto rounded bg-neutral-50 p-2 text-xs text-neutral-800">
                                        {formatAuditJson(entry.postValues)}
                                    </pre>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
            <div className="mt-6 flex justify-end border-t border-neutral-100 pt-4">
                <Button type="button" onClick={onClose}>
                    Close
                </Button>
            </div>
        </DialogFrame>
    );
}
